// CSV parser that follows RFC 4180 except that field values are trimmed of
// leading/trailing whitespace (RFC says spaces are part of a field, but trimming
// is correct for AWS Organizations exports where column names may vary in spacing).
// Scans the raw text without pre-splitting on newlines so that quoted fields
// containing embedded newlines are handled correctly.
function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let cols: string[] = [];
  let cur = '';
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const next = text[i + 1];

    if (inQuotes) {
      if (ch === '"' && next === '"') {
        cur += '"';
        i++;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      cols.push(cur.trim());
      cur = '';
    } else if (ch === '\n') {
      cols.push(cur.trim());
      cur = '';
      if (cols.some((c) => c !== '')) rows.push(cols);
      cols = [];
    } else if (ch !== '\r') {
      cur += ch;
    }
  }

  if (inQuotes) {
    throw new Error(
      'The CSV file contains an unclosed quoted field. The file may be corrupted or truncated.'
    );
  }

  cols.push(cur.trim());
  if (cols.some((c) => c !== '')) rows.push(cols);
  return rows;
}

const AWS_ACCOUNT_ID_RE = /^\d{12}$/;

// Parses an AWS Organizations account export CSV and returns the ACTIVE account IDs.
// Also accepts a plain single-column list of 12-digit account IDs (with or without
// a header row). When a structured "Account ID" + "Status" header is detected the
// status column is used to filter; otherwise every valid 12-digit ID is included.
// Throws with a descriptive message if the file is invalid or contains no usable accounts.
export function parseAwsAccountsCsv(csvText: string): string[] {
  const rows = parseCsv(csvText);
  if (rows.length === 0) {
    throw new Error(
      'The file appears to be empty. Export your account list from AWS Organizations (Actions → Export account list) or provide a list of 12-digit account IDs and try again.'
    );
  }

  const header = rows[0].map((h) => h.toLowerCase());
  const accountIdIdx = header.findIndex((h) => h === 'account id');

  if (accountIdIdx !== -1) {
    return parseStructuredCsv(rows, accountIdIdx, header);
  }

  return parsePlainList(rows);
}

// Structured AWS Organizations export with "Account ID" (and optional "Status") columns.
function parseStructuredCsv(
  rows: string[][],
  accountIdIdx: number,
  header: string[]
): string[] {
  if (rows.length < 2) {
    throw new Error(
      'The file has a header row but no data rows. Export your account list from AWS Organizations (Actions → Export account list) and try again.'
    );
  }

  const statusIdx = header.findIndex((h) => h === 'status');
  const accountIds: string[] = [];
  const seen = new Set<string>();
  let skippedInactive = 0;

  for (let i = 1; i < rows.length; i++) {
    const cols = rows[i];
    const accountId = cols[accountIdIdx] ?? '';

    if (!AWS_ACCOUNT_ID_RE.test(accountId)) continue;
    if (seen.has(accountId)) continue;
    seen.add(accountId);

    const status = statusIdx !== -1 ? (cols[statusIdx] ?? 'ACTIVE') : 'ACTIVE';
    if (status.toUpperCase() !== 'ACTIVE') {
      skippedInactive++;
      continue;
    }

    accountIds.push(accountId);
  }

  if (accountIds.length === 0) {
    if (skippedInactive > 0) {
      throw new Error(
        `Found ${skippedInactive} account${skippedInactive !== 1 ? 's' : ''} but none are ACTIVE. Only active accounts can be onboarded.`
      );
    }
    throw new Error(
      'No valid account IDs found. Each account ID must be a 12-digit number.'
    );
  }

  return accountIds;
}

// Plain single-column list of account IDs. If the first row is not a 12-digit
// number it is treated as a header and skipped; otherwise all rows are scanned.
function parsePlainList(rows: string[][]): string[] {
  const firstValue = rows[0]?.[0] ?? '';
  const startIdx = AWS_ACCOUNT_ID_RE.test(firstValue) ? 0 : 1;

  const accountIds: string[] = [];
  const seen = new Set<string>();

  for (let i = startIdx; i < rows.length; i++) {
    const accountId = rows[i][0] ?? '';
    if (!AWS_ACCOUNT_ID_RE.test(accountId)) continue;
    if (seen.has(accountId)) continue;
    seen.add(accountId);
    accountIds.push(accountId);
  }

  if (accountIds.length === 0) {
    throw new Error(
      'No valid account IDs found. Each account ID must be a 12-digit number.'
    );
  }

  return accountIds;
}
